import { lightImpactVibrate } from '../../../../../../../utils/appBridge'
import { SipActionFilter } from '../../../api/types'

export const SIP_PLANS_ACTION_FILTERS_CLASS_NAME = 'relative z-[1] pt-[18px]'

export const DEFAULT_SIP_ACTION_FILTERS: SipActionFilter[] = [
  SipActionFilter.ALL,
  SipActionFilter.STOP,
  SipActionFilter.START,
  SipActionFilter.INCREASE,
  SipActionFilter.DECREASE,
  SipActionFilter.NO_ACTION
]

const SIP_ACTION_FILTER_LABELS: Record<SipActionFilter, string> = {
  [SipActionFilter.ALL]: 'All actions',
  [SipActionFilter.STOP]: 'Stop',
  [SipActionFilter.START]: 'Start',
  [SipActionFilter.INCREASE]: 'Increase',
  [SipActionFilter.DECREASE]: 'Decrease',
  [SipActionFilter.NO_ACTION]: 'No action'
}

type ListSipActionFiltersProps = {
  availableFilters: SipActionFilter[]
  activeFilter: SipActionFilter
  onFilterChange: (filter: SipActionFilter) => void
}

export function ListSipActionFilters({
  availableFilters,
  activeFilter,
  onFilterChange
}: ListSipActionFiltersProps) {
  if (availableFilters.length <= 0) {
    return null
  }

  return (
    <div className="relative z-[1] overflow-x-auto no-scrollbar overscroll-x-none px-5 pb-6 pt-1">
      <div className="flex gap-[10px] w-max">
        {availableFilters.map((filterOption) => {
          const isSelected = activeFilter === filterOption
          return (
            <div
              key={filterOption}
              className="flex w-[58px] shrink-0 flex-col items-center gap-3 cursor-pointer"
              onClick={() => {
                lightImpactVibrate()
                onFilterChange(filterOption)
              }}
            >
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full ${isSelected ? 'bg-[#232323]' : 'bg-[#FDFCFE]'
                  }`}
              >
                <ListSipActionFilterIcon filter={filterOption} isSelected={isSelected} />
              </div>
              <span
                className={`text-center text-[12px] leading-[1.2] max-w-[64px] ${isSelected ? 'font-[500] text-[#232323]' : 'text-[#6A6E6E] opacity-50'
                  }`}
              >
                {SIP_ACTION_FILTER_LABELS[filterOption]}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ListSipActionFilterIcon({
  filter,
  isSelected
}: {
  filter: SipActionFilter
  isSelected: boolean
}) {
  const iconColor = isSelected ? '#FFFFFF' : '#6A6E6E'

  if (filter === SipActionFilter.ALL) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M1 8L4.93844 11.5L12.2667 2.5"
          stroke={iconColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (filter === SipActionFilter.STOP) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M1.10938 7L10.1719 7" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" />
        <path
          d="M5.1875 2.01562L10.1719 7L5.1875 11.9844"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path d="M12.8906 11.9844L12.8906 2.01562" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  if (filter === SipActionFilter.START) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M1.10938 7L10.1719 7" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" />
        <path
          d="M5.1875 2.01562L10.1719 7L5.1875 11.9844"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path d="M12.8906 11.9844L12.8906 2.01562" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  if (filter === SipActionFilter.INCREASE) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M5.93524 14.4252C6.70357 14.9626 7.61038 15.3008 8.58819 15.3725V17C7.18014 16.9198 5.88297 16.4082 4.816 15.5897L5.93524 14.4252ZM10.115 1.14786L8.91127 2.40037H8.97829C12.8561 2.40048 16 5.67136 16 9.7061C16 
        13.3259 13.4692 16.3289 10.1486 16.9088V15.2552C12.602 14.6975 14.4396 12.4261 14.4396 9.7061C14.4396 6.568 11.9944 4.02398 8.97829 4.02387H8.91127L10.115 5.27638L9.01184 6.42424L6.47619 3.78605C6.32988 3.63382 6.24762 3.42741 
        6.24762 3.21212C6.24764 2.99685 6.32989 2.79041 6.47619 2.63819L9.01184 0L10.115 1.14786ZM3.57257 10.5178C3.71863 11.5802 4.14715 12.5475 4.77639 13.3352L3.6701 14.4863C2.76335 13.3972 2.1604 12.0274 2 10.5178H3.57257ZM3.99772 5.79244L4.61943 
        6.28155C4.07545 7.0298 3.70606 7.92203 3.57257 8.89434H2.00076C2.14329 7.55512 2.63286 6.32368 3.37524 5.30253L3.99772 5.79244Z" fill={iconColor} />
      </svg>
    )
  }

  if (filter === SipActionFilter.DECREASE) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M5.93524 14.4252C6.70357 14.9626 7.61038 15.3008 8.58819 15.3725V17C7.18014 16.9198 5.88297 16.4082 4.816 15.5897L5.93524 14.4252ZM10.115 1.14786L8.91127 2.40037H8.97829C12.8561 2.40048 16 5.67136 16 9.7061C16 
        13.3259 13.4692 16.3289 10.1486 16.9088V15.2552C12.602 14.6975 14.4396 12.4261 14.4396 9.7061C14.4396 6.568 11.9944 4.02398 8.97829 4.02387H8.91127L10.115 5.27638L9.01184 6.42424L6.47619 3.78605C6.32988 3.63382 6.24762 3.42741 
        6.24762 3.21212C6.24764 2.99685 6.32989 2.79041 6.47619 2.63819L9.01184 0L10.115 1.14786ZM3.57257 10.5178C3.71863 11.5802 4.14715 12.5475 4.77639 13.3352L3.6701 14.4863C2.76335 13.3972 2.1604 12.0274 2 10.5178H3.57257ZM3.99772 5.79244L4.61943 
        6.28155C4.07545 7.0298 3.70606 7.92203 3.57257 8.89434H2.00076C2.14329 7.55512 2.63286 6.32368 3.37524 5.30253L3.99772 5.79244Z" fill={iconColor} />
      </svg>
    )
  }

  if (filter === SipActionFilter.NO_ACTION) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
        <path
          d="M14.8198 7.9542V4.07415H10.3739V-0.00130177H7.42947L7.20658 0.27079L4.09499 4.07415H0V13.707H11.9434L14.8198 7.9542ZM5.18694 5.07997L8.13134 1.48068H8.89189V5.55613H13.3378V7.60401L11.0273 12.225H5.18694V5.07997ZM1.48198 5.55613H3.70496V12.225H1.48198V5.55613Z"
          fill={iconColor}
        />
      </svg>
    )
  }

  return null
}
